/**
 * NotificationItem — individual notification card.
 *
 * Implements AC36 visual states: unread (blue-50 + border), read (dimmed),
 * hover actions (mark read, archive), keyboard navigation, auto-read on click.
 */

import React, { useState } from 'react';
import { Check, Archive, Bell, AlertCircle, BookOpen, Megaphone, Settings } from 'lucide-react';
import type { Notification, NotificationCategory } from '../../api/notifications.js';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
  /** Last item ref for infinite scroll observation */
  lastItemRef?: (node: HTMLElement | null) => void;
}

function getCategoryIcon(category: NotificationCategory): React.ReactNode {
  switch (category) {
    case 'ticket_update':
    case 'ticket_assignment':
      return <AlertCircle className="w-4 h-4 text-blue-500" />;
    case 'incident_alert':
    case 'incident_resolved':
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    case 'learning_reminder':
    case 'learning_completion':
      return <BookOpen className="w-4 h-4 text-green-500" />;
    case 'product_update':
    case 'onboarding':
      return <Megaphone className="w-4 h-4 text-purple-500" />;
    case 'system':
    case 'admin':
      return <Settings className="w-4 h-4 text-gray-500" />;
    default:
      return <Bell className="w-4 h-4 text-gray-400" />;
  }
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

export function NotificationItem({
  notification,
  onMarkRead,
  onArchive,
  lastItemRef,
}: NotificationItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (notification.action_url) {
      window.open(notification.action_url, '_blank', 'noopener,noreferrer');
    }
    // Auto-mark as read after 500ms
    if (!notification.read) {
      setTimeout(() => onMarkRead(notification.id), 500);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const relativeTime = formatRelativeTime(notification.created_at);

  return (
    <article
      ref={lastItemRef}
      role="article"
      aria-label={`${notification.title}, ${relativeTime}${notification.read ? '' : ', unread'}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative px-4 py-3 cursor-pointer transition-all duration-150
        border-l-4 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500
        ${notification.read
          ? 'bg-white opacity-70 border-transparent hover:bg-gray-50 hover:opacity-100'
          : 'bg-blue-50 border-blue-500 hover:bg-blue-100'
        }
      `}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator dot */}
        {!notification.read && (
          <span
            aria-hidden="true"
            className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"
          />
        )}

        {/* Category icon */}
        <span aria-hidden="true" className={`mt-0.5 flex-shrink-0 ${notification.read ? 'ml-5' : ''}`}>
          {getCategoryIcon(notification.category)}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${notification.read ? 'text-gray-700' : 'text-gray-900'}`}>
            {notification.title}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
            {notification.body}
          </p>
          <p className="text-xs text-gray-400 mt-1">{relativeTime}</p>
        </div>

        {/* Action buttons — visible on hover or focus */}
        {isHovered && (
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {!notification.read && (
              <button
                type="button"
                onClick={() => onMarkRead(notification.id)}
                aria-label="Mark as read"
                title="Mark as read"
                className="
                  p-1 rounded text-gray-400 hover:text-green-600 hover:bg-green-50
                  transition-colors focus:outline-none focus:ring-1 focus:ring-green-500
                "
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onArchive(notification.id)}
              aria-label="Archive notification"
              title="Archive"
              className="
                p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50
                transition-colors focus:outline-none focus:ring-1 focus:ring-red-500
              "
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
